/**
 * NarrationViewer — Viewer + Editor for audio_guide posts.
 * Supports v4 character system: switch between narration characters.
 * Read mode: renders knowledge JSON in structured format.
 * Edit mode: inline textareas for each narrative text.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Post } from '../types';
import { STORAGE_API } from '../config';

type ViewMode = 'read' | 'edit';

interface NarrationViewerProps {
  post: Post;
  viewMode: ViewMode;
  onSave: (newContent: string) => void;
  isSaving: boolean;
}

interface NarrativeText {
  text?: string;
  text_original?: string;
  edited?: boolean;
  cues?: { index: number; text: string; audio_storage_id?: number; duration_seconds?: number }[];
  total_duration?: number;
}

interface CharacterDef {
  id: string;
  name: string;
  persona?: string;
  voice?: string;
  is_default?: boolean;
}

interface KnowledgeItem {
  name?: string;
  description?: string;
  id?: number;
  waypoint_id?: number;
  start_waypoint_id?: number;
  end_waypoint_id?: number;
  narrations?: Record<string, Record<string, NarrativeText>>;
  // v3 flat keys (backward compat)
  intro?: NarrativeText;
  outro?: NarrativeText;
  entry?: NarrativeText;
  exit?: NarrativeText;
  approaching?: NarrativeText;
  at_poi?: NarrativeText;
}

interface KnowledgeContent {
  version?: number;
  characters?: CharacterDef[];
  config?: {
    persona?: string;
    target_audience?: string;
    language?: string;
    tone?: string;
    background_knowledge?: string;
  };
  routes?: Record<string, KnowledgeItem>;
  segments?: Record<string, KnowledgeItem>;
  pois?: Record<string, KnowledgeItem>;
}

const STORAGE_BASE = `${STORAGE_API}/storage/media`;

/** Get narrative text for a specific character, with v3 fallback */
function getCharacterText(
  item: KnowledgeItem,
  field: string,
  characterId: string | null
): NarrativeText | undefined {
  // v4: try narrations.characterId.field
  if (characterId && item.narrations?.[characterId]?.[field]) {
    return item.narrations[characterId][field];
  }
  // v3 fallback: flat keys
  return (item as Record<string, unknown>)[field] as NarrativeText | undefined;
}

function CuesDisplay({ cues, totalDuration }: { cues?: NarrativeText['cues']; totalDuration?: number }) {
  if (!cues || cues.length === 0) return null;
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {cues.map((cue) => (
        <div key={cue.index} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <span style={{ color: '#9ca3af', minWidth: 24 }}>#{cue.index + 1}</span>
          {cue.audio_storage_id && (
            <audio controls preload="none" style={{ height: 28 }}>
              <source src={`${STORAGE_BASE}/${cue.audio_storage_id}`} />
            </audio>
          )}
          {cue.duration_seconds != null && (
            <span style={{ color: '#9ca3af' }}>{cue.duration_seconds.toFixed(1)}s</span>
          )}
        </div>
      ))}
      {totalDuration != null && (
        <span style={{ fontSize: 11, color: '#9ca3af' }}>Gesamt: {totalDuration.toFixed(1)}s</span>
      )}
    </div>
  );
}

function NarrativeBlockRead({ label, data }: { label: string; data?: NarrativeText }) {
  if (!data?.text) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
        {data.edited && <span style={{ marginLeft: 6, fontSize: 10, color: '#d97706', fontWeight: 500 }}>bearbeitet</span>}
      </div>
      <p style={{ fontSize: 14, lineHeight: 1.6, color: '#1a1a1a', margin: 0 }}>{data.text}</p>
      <CuesDisplay cues={data.cues} totalDuration={data.total_duration} />
    </div>
  );
}

function NarrativeBlockEdit({ label, data, onChange }: {
  label: string;
  data?: NarrativeText;
  onChange: (text: string) => void;
}) {
  const text = data?.text || '';
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
        {data?.edited && <span style={{ marginLeft: 6, fontSize: 10, color: '#d97706', fontWeight: 500 }}>bearbeitet</span>}
      </div>
      <textarea
        value={text}
        onChange={(e) => onChange(e.target.value)}
        rows={Math.max(2, Math.ceil(text.length / 80))}
        style={{
          width: '100%', padding: 8, fontSize: 14, lineHeight: 1.6,
          border: '1px solid #d1d5db', borderRadius: 6, resize: 'vertical',
          fontFamily: 'inherit', color: '#1a1a1a',
        }}
        placeholder={`${label} Text...`}
      />
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, textAlign: 'right' }}>
        {text.length} Zeichen
      </div>
      <CuesDisplay cues={data?.cues} totalDuration={data?.total_duration} />
    </div>
  );
}

/** Character selector tabs */
function CharacterSelector({ characters, activeId, onChange }: {
  characters: CharacterDef[];
  activeId: string;
  onChange: (id: string) => void;
}) {
  if (characters.length <= 1) return null;

  const COLORS: Record<string, string> = {
    dr_tschauko: '#6366f1',
    tschauko_tschauka: '#f59e0b',
  };

  return (
    <div style={{
      display: 'flex', gap: 8, marginBottom: 20, padding: '12px 16px',
      background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0',
      alignItems: 'center',
    }}>
      <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500, marginRight: 4 }}>
        Guide:
      </span>
      {characters.map((char) => {
        const isActive = char.id === activeId;
        const color = COLORS[char.id] || '#6b7280';
        return (
          <button
            key={char.id}
            onClick={() => onChange(char.id)}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: isActive ? `2px solid ${color}` : '2px solid transparent',
              background: isActive ? `${color}15` : '#fff',
              color: isActive ? color : '#6b7280',
              fontWeight: isActive ? 600 : 400,
              fontSize: 13,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {char.name}
          </button>
        );
      })}
    </div>
  );
}

/** Count filled texts for a character */
function countTexts(knowledge: KnowledgeContent, characterId: string | null): { filled: number; total: number } {
  let filled = 0;
  let total = 0;

  const routeFields = ['intro', 'outro'];
  const segFields = ['entry', 'exit'];
  const poiFields = ['approaching', 'at_poi'];

  for (const item of Object.values(knowledge.routes || {})) {
    for (const f of routeFields) {
      total++;
      if (getCharacterText(item, f, characterId)?.text) filled++;
    }
  }
  for (const item of Object.values(knowledge.segments || {})) {
    for (const f of segFields) {
      total++;
      if (getCharacterText(item, f, characterId)?.text) filled++;
    }
  }
  for (const item of Object.values(knowledge.pois || {})) {
    for (const f of poiFields) {
      total++;
      if (getCharacterText(item, f, characterId)?.text) filled++;
    }
  }

  return { filled, total };
}

export function NarrationViewer({ post, viewMode, onSave, isSaving }: NarrationViewerProps) {
  const [knowledge, setKnowledge] = useState<KnowledgeContent>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [activeCharacter, setActiveCharacter] = useState<string>('');
  const [sectionFilter, setSectionFilter] = useState<'all' | 'routes' | 'segments' | 'pois'>('all');

  useEffect(() => {
    try {
      const k = post.content ? JSON.parse(post.content) : {};
      setKnowledge(k);
      setHasChanges(false);
      // Set default character
      const chars = k.characters || [];
      const defaultChar = chars.find((c: CharacterDef) => c.is_default) || chars[0];
      if (defaultChar) setActiveCharacter(defaultChar.id);
    } catch {
      setKnowledge({});
    }
  }, [post.content]);

  const characters = useMemo(() => knowledge.characters || [], [knowledge.characters]);
  const charId = activeCharacter || null;

  const stats = useMemo(() => countTexts(knowledge, charId), [knowledge, charId]);

  const updateText = useCallback((
    section: 'routes' | 'segments' | 'pois',
    key: string,
    field: string,
    text: string
  ) => {
    setKnowledge(prev => {
      const next = JSON.parse(JSON.stringify(prev)) as KnowledgeContent;
      const items = next[section] as Record<string, KnowledgeItem> | undefined;
      if (!items || !items[key]) return prev;
      const item = items[key];

      // Write to narrations.characterId.field (v4)
      if (charId) {
        if (!item.narrations) item.narrations = {};
        if (!item.narrations[charId]) item.narrations[charId] = {};
        const existing = item.narrations[charId][field];
        item.narrations[charId][field] = {
          ...existing,
          text,
          edited: text !== (existing?.text_original || ''),
        };
      }

      // Also write flat key for backward compat (only for default character)
      const isDefault = characters.find(c => c.id === charId)?.is_default ?? true;
      if (isDefault) {
        const flat = (item as Record<string, unknown>)[field] as NarrativeText | undefined;
        if (flat) {
          flat.text = text;
          flat.edited = text !== (flat.text_original || '');
        } else {
          (item as Record<string, unknown>)[field] = { text, edited: true };
        }
      }

      return next;
    });
    setHasChanges(true);
  }, [charId, characters]);

  const handleSave = useCallback(() => {
    onSave(JSON.stringify(knowledge, null, 2));
    setHasChanges(false);
  }, [knowledge, onSave]);

  const config = knowledge.config;
  const routeEntries = Object.entries(knowledge.routes || {});
  const segmentEntries = Object.entries(knowledge.segments || {});
  const poiEntries = Object.entries(knowledge.pois || {});

  const isEdit = viewMode === 'edit';

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Save bar in edit mode */}
      {isEdit && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 10,
          padding: '8px 0', marginBottom: 16,
          background: '#fafaf8', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            style={{
              padding: '6px 16px', fontSize: 13, fontWeight: 600,
              borderRadius: 6, border: 'none', cursor: hasChanges ? 'pointer' : 'default',
              background: hasChanges ? '#10b981' : '#d1d5db',
              color: hasChanges ? '#fff' : '#9ca3af',
            }}
          >
            {isSaving ? 'Speichern...' : 'Speichern'}
          </button>
          {hasChanges && <span style={{ fontSize: 12, color: '#d97706' }}>Ungespeicherte Änderungen</span>}
        </div>
      )}

      {/* Character Selector */}
      <CharacterSelector
        characters={characters}
        activeId={activeCharacter}
        onChange={setActiveCharacter}
      />

      {/* Stats */}
      {stats.total > 0 && (
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16 }}>
          {stats.filled}/{stats.total} Texte gefüllt
          {characters.length > 1 && charId && (
            <span> für <strong>{characters.find(c => c.id === charId)?.name}</strong></span>
          )}
        </div>
      )}

      {/* Section Filter */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap',
      }}>
        {[
          { key: 'all' as const, label: 'Alle', count: Object.keys(knowledge.routes || {}).length + Object.keys(knowledge.segments || {}).length + Object.keys(knowledge.pois || {}).length },
          { key: 'routes' as const, label: 'Routen', count: Object.keys(knowledge.routes || {}).length, color: '#3b82f6' },
          { key: 'segments' as const, label: 'Segmente', count: Object.keys(knowledge.segments || {}).length, color: '#10b981' },
          { key: 'pois' as const, label: 'POIs', count: Object.keys(knowledge.pois || {}).length, color: '#f59e0b' },
        ].map(({ key, label, count, color }) => (
          <button
            key={key}
            onClick={() => setSectionFilter(key)}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: sectionFilter === key ? `2px solid ${color || '#6366f1'}` : '2px solid transparent',
              background: sectionFilter === key ? `${color || '#6366f1'}15` : '#f1f5f9',
              color: sectionFilter === key ? (color || '#6366f1') : '#6b7280',
              fontWeight: sectionFilter === key ? 600 : 400,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {label} ({count})
          </button>
        ))}
      </div>

      {/* Config */}
      {config && (config.persona || config.target_audience) && (
        <section style={{ marginBottom: 32, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#475569' }}>Konfiguration</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
            {/* Show character persona instead of global persona if available */}
            {charId && characters.find(c => c.id === charId)?.persona ? (
              <div style={{ gridColumn: '1 / -1' }}>
                <span style={{ color: '#9ca3af' }}>Persona ({characters.find(c => c.id === charId)?.name}):</span>{' '}
                {characters.find(c => c.id === charId)?.persona}
              </div>
            ) : config.persona ? (
              <div><span style={{ color: '#9ca3af' }}>Persona:</span> {config.persona}</div>
            ) : null}
            {config.target_audience && <div><span style={{ color: '#9ca3af' }}>Zielgruppe:</span> {config.target_audience}</div>}
            {config.language && <div><span style={{ color: '#9ca3af' }}>Sprache:</span> {config.language.toUpperCase()}</div>}
            {config.tone && <div><span style={{ color: '#9ca3af' }}>Ton:</span> {config.tone}</div>}
          </div>
        </section>
      )}

      {/* Routes */}
      {routeEntries.length > 0 && (sectionFilter === 'all' || sectionFilter === 'routes') && (
        <section style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #e2e8f0' }}>
            Routen ({routeEntries.length})
          </h3>
          {routeEntries.map(([key, route]) => (
            <div key={key} style={{ marginBottom: 24, paddingLeft: 12, borderLeft: '3px solid #3b82f6' }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                {route.name}
                {route.description && <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8, fontSize: 12 }}>{route.description}</span>}
              </h4>
              {isEdit ? (
                <>
                  <NarrativeBlockEdit label="Intro" data={getCharacterText(route, 'intro', charId)} onChange={(t) => updateText('routes', key, 'intro', t)} />
                  <NarrativeBlockEdit label="Outro" data={getCharacterText(route, 'outro', charId)} onChange={(t) => updateText('routes', key, 'outro', t)} />
                </>
              ) : (
                <>
                  <NarrativeBlockRead label="Intro" data={getCharacterText(route, 'intro', charId)} />
                  <NarrativeBlockRead label="Outro" data={getCharacterText(route, 'outro', charId)} />
                </>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Segments */}
      {segmentEntries.length > 0 && (sectionFilter === 'all' || sectionFilter === 'segments') && (
        <section style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #e2e8f0' }}>
            Segmente ({segmentEntries.length})
          </h3>
          {segmentEntries.map(([key, seg]) => (
            <div key={key} style={{ marginBottom: 24, paddingLeft: 12, borderLeft: '3px solid #10b981' }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                {seg.name}
                {seg.description && <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8, fontSize: 12 }}>{seg.description}</span>}
              </h4>
              {isEdit ? (
                <>
                  <NarrativeBlockEdit label="Entry" data={getCharacterText(seg, 'entry', charId)} onChange={(t) => updateText('segments', key, 'entry', t)} />
                  <NarrativeBlockEdit label="Exit" data={getCharacterText(seg, 'exit', charId)} onChange={(t) => updateText('segments', key, 'exit', t)} />
                </>
              ) : (
                <>
                  <NarrativeBlockRead label="Entry" data={getCharacterText(seg, 'entry', charId)} />
                  <NarrativeBlockRead label="Exit" data={getCharacterText(seg, 'exit', charId)} />
                </>
              )}
            </div>
          ))}
        </section>
      )}

      {/* POIs */}
      {poiEntries.length > 0 && (sectionFilter === 'all' || sectionFilter === 'pois') && (
        <section style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #e2e8f0' }}>
            Points of Interest ({poiEntries.length})
          </h3>
          {poiEntries.map(([key, poi]) => (
            <div key={key} style={{ marginBottom: 24, paddingLeft: 12, borderLeft: '3px solid #f59e0b' }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                {poi.name}
                {poi.description && <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8, fontSize: 12 }}>{poi.description}</span>}
              </h4>
              {isEdit ? (
                <>
                  <NarrativeBlockEdit label="Approaching" data={getCharacterText(poi, 'approaching', charId)} onChange={(t) => updateText('pois', key, 'approaching', t)} />
                  <NarrativeBlockEdit label="At POI" data={getCharacterText(poi, 'at_poi', charId)} onChange={(t) => updateText('pois', key, 'at_poi', t)} />
                </>
              ) : (
                <>
                  <NarrativeBlockRead label="Approaching" data={getCharacterText(poi, 'approaching', charId)} />
                  <NarrativeBlockRead label="At POI" data={getCharacterText(poi, 'at_poi', charId)} />
                </>
              )}
            </div>
          ))}
        </section>
      )}

      {routeEntries.length === 0 && segmentEntries.length === 0 && poiEntries.length === 0 && (
        <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>Noch keine Narrationen vorhanden.</p>
      )}
    </div>
  );
}
